import { DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef
} from "@angular/core";
import { CalendarEvent } from "angular-calendar";
import { diff } from "deep-object-diff";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { asyncScheduler, map, Observable, Subscription, timer } from "rxjs";
import * as XLSX from "xlsx";
import { AppState, ShowRequest } from "../../../core";
import { AppTab } from "../../../core/app-tab";
import { PostProcessingService } from "../../../core/services";
import { RestClient } from "../../../core/services/rest-client";
import { SortCompareFn } from "../sort-field/sort-field.component";
import { TableRecsAction } from "./table-view-action";
import { TableViewConfigComponent } from "./table-view-config/table-view-config.component";
import { RecordDetailModalComponent } from "../record-detail-modal/record-detail-modal.component";
import _ from "lodash";

export type TableViewFilterMode =
  | "contains"
  | "="
  | "!="
  | "*"
  | "!*"
  | ">="
  | "<"
  | "~*"
  | "empty"
  | "not empty";

export const filterModes: TableViewFilterMode[] = [
  "contains",
  "=",
  "!=",
  "*",
  "!*",
  ">=",
  "<",
  "~*",
  "empty",
  "not empty"
];

export type TableViewFilter = {
  key: string;
  value: string;
  mode: TableViewFilterMode;
  timeZone?: string;
  validationError?: string;
  readonly?: boolean;
  cssClass?: string; // use 'd-none' from server filter def to hide a pre-populated value
};

export const filtersToString = (filters: TableViewFilter[]) =>
  "[" + filters?.map((f) => `${f.key}${f.mode}${f.value}`).join(",") + "]";

export type TableViewSort = {
  key: string;
  order: "ASC" | "DESC";
  smallNulls?: boolean; // postgres defaults to having null as bigger than everything else
};

export type TableViewQueryCriteria = {
  filters?: TableViewFilter[];
  sorts?: TableViewSort[];
  page?: {
    size?: number;
    number?: number;
    firstRowFilters?: TableViewFilter[];
  };
  groupByDayKey?: string
  timeZone?: string;
  includeMeta?: boolean;
};

export type TableViewContext = TableViewQueryCriteria & {
  visible: string[]; // column names
  visibleIndices: number[];
  visibleNames?: string[];
  selectedIndices: number[];
  calendarColumnName: string
  selectedViewName?: string;
  statsEnabled?: boolean;
};

export type StatMetric = {
  column: string;
  title?: string;
  hidden?: boolean;
  format?: {
    dataType: 'number'|'date';
    layout?: string;
  }
  statTypes: ('min' | 'max' | 'sum' | 'avg' | 'count')[]
};

export type StatGroup = {
  id: string;
  title?: string;
  visible?: boolean;
  groupBy: string[]; // group by
  metrics: StatMetric[];
};

export type TableViewQueryRequest = TableViewQueryCriteria & {
  requestId: string;
  alternateViewName?: string;
  statGroups?: StatGroup[];
  statsOnly?: boolean; // Flag to indicate if only stats should be returned
};


export type StatGroupResult = StatGroup & {
  rows: any[][];
};

export class TableViewQueryResponse<RecordType> {
  pageSize?: number;
  pageNumber: number;
  tableRowCount: number;
  queryRowCount: number;
  requestId: string;
  rows: RecordType[];
  selectedIndices?: number[];
  groupedByDay?: [ {day: string, day_total: number} ] // for calendar control and browsing data by month
  meta?: TableViewMeta;
  statGroups?: StatGroupResult[];
}

export const UTC_TEMPLATES = ['time','date','fulltime'];

export type CellTemplateName = 'time'|'date'|'fulltime'|'localtime'|'localdate'|'number'|'percent'|'actions';

export type TableHeaderTemplateContext = {
  column: TableColumn;
  table: TableViewComponentBase<any>
}
export class TableColumn {
  title: string;
  name: string;
  visible: boolean;
  sortable?: boolean;
  smallNullSort?: boolean;
  filterable?: boolean;
  template?: string; //CellTemplateName;
  headerTemplateRef?: TemplateRef<TableHeaderTemplateContext>;
  filter?: TableViewFilter;
  sortCompare?: (lhs, rhs) => number;
  type?: string;
  timeZone?: string;
  filterEditFn?: ( applyNewValFn: (newVal:string|null) => void ) => Promise<boolean>;
  headerCellCssClass?: string | { [param: string]: boolean };
  filterCellCssClass?: string | { [param: string]: boolean };
  dataCellCssClass?: string | { [param: string]: boolean };
}

// export type TimeZoneCriteria = {
//   default: string;
//   columns?: { [name:string]:string };
// }

export type AlternateView = {
  name: string;
  title: string;
  cssClass?: string;
  iconClass?: string;
  description?: string;
  dbViewName?: string;
  filters?: TableViewFilter[];
  sorts?: TableViewSort[];
  visibility?: {
    remove?: string[]; // keep UI columns user is looking at except remove these
    add?: string[]; // keep UI columns but add these too
    show?: string[]; // only show these (add/remove should not be used if show has values)
  }
};

export type TableViewMeta = {
  alternateViews: AlternateView[];
}

// way to reset columns when alternatve view chosen
type PristineTableSettings = {
  filters?: TableViewFilter[];
  sorts?: TableViewSort[];
  visibleColNames?: string[];
}

export const numberToFilterString = (n: number) => (n == null ? "" : `${n}`);

// Default component for rendering table tbody td cells
@Component({
  selector: "[fes-table-view-default-cell-renderer]",
  template: `<ng-container [ngSwitch]="column.template">
    <span *ngSwitchCase="'time'" class="text-nowrap">
      {{ record[column.name] | date: "yyyy-MM-dd HH:mm":"UTC" }}
    </span>
    <span *ngSwitchCase="'date'" class="text-nowrap">
      {{ record[column.name] | date: "yyyy-MM-dd":"UTC" }}
    </span>
    <span *ngSwitchCase="'fulltime'" class="text-nowrap">{{
      record[column.name] | date: "yyyy-MM-dd HH:mm:ss.SSS":"UTC"
    }}</span>
    <span *ngSwitchCase="'localdate'" class="text-nowrap">
      {{ record[column.name] | date: "yyyy-MM-dd" }}
    </span>
    <span *ngSwitchCase="'fulllocaltime'" class="text-nowrap">
      {{ record[column.name] | date: "yyyy-MM-dd HH:mm:ss.SSS" }}
    </span>
    <span *ngSwitchCase="'localtime'" class="text-nowrap">
      {{ record[column.name] | date: "yyyy-MM-dd HH:mm" }}
    </span>
    <span *ngSwitchCase="'number'" style="float: right" [attr.title]="record[column.name]">
      {{ record[column.name]|number:'1.0-6' }}
    </span>
    <span *ngSwitchCase="'fixed4'" style="float: right" [attr.title]="record[column.name]">
      {{record[column.name]|number:'1.4'}}
    </span>
    <span *ngSwitchCase="'percent'" style="float: right" [attr.title]="record[column.name]">
      <div *ngIf="record[column.name] === 'n/a'; else elseBlock">
        {{ record[column.name] }}
      </div>
      <ng-template #elseBlock
        >{{ record[column.name] * 100 | number: "1.1-1" }}%</ng-template
      >
    </span>
    <div *ngSwitchCase="'boolean'" class="text-center">
      <i *ngIf="record[column.name]!=null" [attr.title]="record[column.name]"
            [ngClass]="record[column.name]?'fa fa-check-circle text-success':'fa fa-minus text-danger small'">
      </i>
    </div>
    <div *ngSwitchCase="'badboolean'" class="text-center">
      <i *ngIf="record[column.name]!=null" [attr.title]="record[column.name]"
            [ngClass]="record[column.name]?'fa fa-check-circle text-danger':'fa fa-minus text-success small'">
      </i>
    </div>
    <div *ngSwitchCase="'actions'" class="d-flex flex-row">
      <fes-table-view-action-picker
        [rowActions]="tableComponent.singleRowActions"
        [recs]="[record]"
        [table]="tableComponent"
      >
      </fes-table-view-action-picker>
    </div>
    <ng-container *ngSwitchDefault>{{ record[column.name] }}</ng-container>
  </ng-container>`,
  host: { "[class]": "column.dataCellCssClass" },
})
export class DefaultCellRendererComponent<RecordType> {
  @Input()
  column: TableColumn;
  @Input()
  record: RecordType;
  @Input()
  index: number;
  @Input()
  tableComponent: TableViewComponentBase<RecordType>;

  @Output("showRequest") showRequestEmitter = new EventEmitter<ShowRequest>();
}

export type CellTemplateContext<RecordType,TableType=TableViewComponentBase<RecordType>> = {
  record: RecordType,
  column: TableColumn,
  tableComponent: TableType,
  showRequestFn: (req: ShowRequest) => void
}


@Component({ template: "", providers: [PostProcessingService] })
export abstract class TableViewComponentBase<RecordType> {
  constructor(
    public appState: AppState,
    public modalService: BsModalService,
    public elementRef: ElementRef,
    public datePipe: DatePipe
  ) {}

  postProcessingService: PostProcessingService;

  @Input() set tabConfig(tabConfig: AppTab) {
      // some tables may be used as main tab with tabConfig or as child components (potentially null tabConfig)
      if ( tabConfig ) {
      tabConfig.setSelected = this.onTabSelected;
    }
  }

  @Input("detailFn") public set detailFn(fn: (vi: ShowRequest) => void) {
    this.showTabFn = fn;
  }

  @Input() set contextJson(strJson: string) {
    this.parseContextJson(strJson);
  }

  @Input() isActiveView = true;
  @Input() bShowDataRows = true; // way to hide data rows for main table but still show stats

  @Output("contextJsonChange") contextChangeEmitter = new EventEmitter<string>();

  @Input("cellRendererTmplRef") cellRendererTmplRef: TemplateRef<CellTemplateContext<RecordType>>;

  @Input() pagingEnabled = true;

  @Input() statsEnabled = false;
  @Input() statGroups: StatGroup[] = [];

  @Input() loadOnInit = false;

  @Output('recordsLoaded') records$ = new EventEmitter<RecordType[]>()
  @Output('queryResp') queryResp$ = new EventEmitter<TableViewQueryResponse<RecordType>>();

  @Output() rowMouseOverEmitter = new EventEmitter<{record:RecordType,event:MouseEvent}>();
  @Output() rowMouseOutEmitter = new EventEmitter<{record:RecordType,event:MouseEvent}>();

  @Input('loadTrigger') set loadTriggerSetter( trigger$: EventEmitter<void> ){
    this.loadTriggerSub = trigger$?.subscribe( () => this.load() )
  };

  @Input() alternateViewsEnabled = true;
  @Input() hideCellRenderer = false; // if you just want to add a <tr *ngFor="let record of table.records"> without fancy cell renderes

  //TODO - rename? (currently a single row action just a bulk action with one record so might be confusing)
  setRowActions(rowActions: TableRecsAction<RecordType>[]) {
    this.bulkRowActions = rowActions.filter((a) => a.isBulkable);
    this.singleRowActions = [...rowActions];
  }

  bulkRowActions: TableRecsAction<RecordType>[] = [];
  isBulkActionProcessing = false;
  singleRowActions: TableRecsAction<RecordType>[] = [];
  isLoading = false;

  filterModes = filterModes

  bStickyBulkActions = false; // keep bulk action buttons column visible on right even if no room for all columns
  records: RecordType[] = []; // current page of query results
  sortColumns: TableViewSort[] = [];
  maxSortColumns = 3; // sort on up to 3 columns (last clicked is first in sortColumns array)
  @Input() pageSize: number | undefined;
  defaultPageSize: number = 40;
  pageNumber = 1;
  pageCount = 0;
  queryRowCount = 0; // ignores page size (all rows matching query)
  tableRowCount = 0;
  lastFilterValues: any = {};
  filterChangeSubscription: Subscription;
  populatedFilterCnt = 0;
  errorMsg: string;
  latestLoadRequestId: string;
  downloadRollupSummLoading: boolean = false;

  tableColumns: TableColumn[] = [];
  columnByName: { [name: string]: TableColumn } = {};
  visibleColumns: TableColumn[] = [];
  idColumns: TableColumn[] = [];
  selectedIndices: number[] = []; // for highlighting/scrolling/paging to a row (not for checkboxes)
  filterKeyUp$ = new EventEmitter<{event:KeyboardEvent,filter:TableViewFilter} >()
  virtualColNames: { [name: string]: string } = {}; // allows multile columns for same db field (ex show date in local and utc)
  meta: TableViewMeta = null; // information from server about custom filters for this table
  selectedAlternateViewName?: string;
  selectedAlternateViewTitle?: string;

  tableCellRenderer: any = DefaultCellRendererComponent;

  checkedRecs: RecordType[] = [];
  allRowsCheckboxValue = false;
  groupedByDay: {day: string, day_total: number}[] = []
  bShowBulkActionsColumn = true;
  bShowHeader = true;
  bShowFilterRow = true;
  loadTriggerSub: Subscription;
  pristineSettings: PristineTableSettings = {};

  statGroupResults: StatGroupResult[] = [];

  showTabFn = (vi: ShowRequest) => {
    this.appState.showView.next( vi );
  }

  visibleColumnsTrackByFn = (index: number, column: TableColumn): any => {
    return column.name;
  }

  rowTrackByFn = (index: number, item: any): any => {
    if ( this.idColumns?.length == 1 ) {
      return item[this.idColumns[0].name];
    } else {
      return item;
    }
  }

  abstract doQuery(
    queryRequest: TableViewQueryRequest
  ): Promise<TableViewQueryResponse<RecordType>>;

  reportErrorFn = (error: any) => {
    this.errorMsg = RestClient.extractErrorMessage(error);
    this.appState.publishErrorObj(error, this.errorMsg, this, 60);
  };

  readonly pageSizeOptions = [10, 20, 40, 50, 100, 500, 1000, 1500, 2000, null];

  defaultVisibleColumnsIndices: number[] = null;

  init() {
    this.visibleColumns = this.tableColumns.filter((c)=>!!c.visible);
    if ( !this.defaultVisibleColumnsIndices ) {
      this.defaultVisibleColumnsIndices = this.getVisibleIndices();
    }
    this.columnByName = {};
    this.tableColumns.forEach((c) => (this.columnByName[c.name] = c));
    if ( this.pageSize == null ) {
      this.pageSize = this.defaultPageSize;
    }
    this.initPristine();
    if ( this.loadOnInit ) {
      this.load();
    }
  }

  initPristine(){
    if ( !this.pristineSettings.filters ) {
      this.pristineSettings.filters = this.tableColumns.filter(c=>c.filter).map(c=>c.filter&&{...c.filter});
    }
    if ( !this.pristineSettings.sorts ) {
      this.pristineSettings.sorts = [...this.sortColumns];
    }
    if ( !this.pristineSettings.visibleColNames ) {
      this.pristineSettings.visibleColNames = this.tableColumns.filter(c=>c.visible).map(c=>c.name);
    }
  }

  destroy() {
    this.filterChangeSubscription?.unsubscribe();
    this.loadTriggerSub?.unsubscribe();
  }

  defaultCalendarDateFilterResolver = (dateColumn: TableColumn) : string => {
    //TODO - need to support finding newest when multiple pages (currently gets news from page one)...
    // this could be async and run table query with page size=1 and sort equal to DESC on dateColumn.name
    if ( this.records?.length && dateColumn?.name ) {
      const key = dateColumn.name;
      const maxMonthRec = this.records.reduce( (newestRec,rec)=>
          SortCompareFn(newestRec[key],rec[key])>0?newestRec:rec, this.records[0]);
      const newestDate = this.datePipe.transform(maxMonthRec[key],'yyyy-MM-dd')
      if ( newestDate ) {
        return newestDate;
      }
    }
    return "";
  }

  createColumnTitle(name: any) {
    const capitalize = (str) =>
      str.replace(/(?:^|\s|-|["'([{])+\S/g, (match) => match.toUpperCase());
    // guess at some common words and add spaces!!!
    name = name.replaceAll(
      /(file|call|time|start|building|point|path|route|floor|import|imei|location|num$|pressure|source|error|valid|uncertainty|phone|summary)/g,
      " $1 "
    );
    return capitalize(name);
  }

  columnStateChanged(column: TableColumn) {
    console.log("columnStateChanged(" + column?.title + ")");
    this.visibleColumns = this.tableColumns.filter((c) => c.visible);
    this.allVisible = this.visibleColumns.length == this.tableColumns.length;
    this.updateContext();
  }

  editCustomFilter = async ( tableColumn: TableColumn, destFilter?: TableViewFilter) => {
    const bEdited = await tableColumn.filterEditFn(destFilter&&((newVal)=> destFilter.value = newVal));
    if ( bEdited === true ) {
      // reload table if change is directly on tableColumn's filter (not config modal's text input)
      if ( !destFilter || destFilter == tableColumn.filter) {
        // no explicit destFilter (such as from table-view-config modal)
        await this.load();
      }
    }
  }

  bInitialized = false;
  initialize() {
    console.log("initialize()");
    if (!this.bInitialized) {
      this.bInitialized = true;
      this.appState.doWhenReady(async () => {
        console.log("initialize() ready.");
        await this.load();
      });
    }
  }

  onFilterKeyUp( $event: {event:KeyboardEvent,filter:TableViewFilter} ){
    this.filterKeyUp$.emit($event)
    if ( $event.event.key == 'Enter' ) {
      this.load()
    }
  }

  filtersModified() {
    const diffObj = diff(this.lastFilterValues, this.buildPopulatedFilters());
    return Object.keys(diffObj).length > 0;
  }

  // Update specific UI rows from an array of rows (i.e. results of a bulk action)
  updateRows = (rows: RecordType[]) => {
    console.log(`updateRows() count:${rows?.length}`);
    if (!this.idColumns?.length) {
      throw new Error(
        "Table must define id columns if updating specific table rows from a data set"
      );
    }
    for (const row of rows) {
      let uiRecs = this.records.filter((record) =>
        this.idColumns.every(
          (idColumn) => row[idColumn.name] == record[idColumn.name]
        )
      );
      const recMatchCnt = uiRecs.length
      if ( !recMatchCnt ) {
        const msg = `UI table could not be updated using key: ${this.idColumns?.map(c=>c.name)}`
        console.warn(msg);
        console.dir(row);
        //throw new Error(msg);
      } else if (uiRecs.length == 1) {
        Object.assign(uiRecs[0], row);
      } else if (uiRecs.length > 1) {
        throw new Error(
          "Multiple rows in table matched row from server based on UI table idColumns=" +
            this.idColumns.map((c) => c.name)
        );
      }
    }
  };

  private buildRequest(filters: TableViewFilter[], requestId?: string): TableViewQueryRequest {
    if (!filters) {
      filters = this.buildPopulatedFilters();
      // clear out text fields for 'emtpy' and 'not empty'
      filters.forEach(f=>{
        if ( ['empty','not empty'].includes(f.mode) ){
          delete f.value
        }
      })
    }
    const queryReq: TableViewQueryRequest = {
      requestId: requestId || "" + new Date().getTime(),
      filters: filters.filter((f) => f.value || ['empty','not empty'].includes(f.mode)).map(f=>{
        const realName = this.virtualColNames[f.key];
        return realName ? { ...f, key: realName } : f;
      }),
      sorts: _.uniqBy(this.sortColumns.map(sc=>{
        const realName = this.virtualColNames[sc.key];
        return realName ? { ...sc, key: realName } : sc;
      }), (sc)=>sc.key) || [],
    };
    if ( this.bShowCalendar && this.calendarColumn ) {
      queryReq.groupByDayKey = this.calendarColumn.name
    }

    queryReq.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (this.firstRowFilters?.length) {
      queryReq.page = {
        size: this.pageSize,
        firstRowFilters: this.firstRowFilters,
      };
    } else if (this.pageSize) {
      queryReq.page = { size: this.pageSize, number: this.pageNumber };
    }
    if ( !this.meta ) {
      queryReq.includeMeta = true;
    }
    if ( this.selectedAlternateViewName != null ) {
       queryReq.alternateViewName = this.selectedAlternateViewName;
    }

    return queryReq;
  }

  loadStats = async (statGroups: StatGroup[], filters: TableViewFilter[] = null) => {
    //console.log(`loadStats()`);
    try {
      // this.isLoading = true;
      this.errorMsg = "";
      const queryReq = this.buildRequest(filters);
      queryReq.statsOnly = true;
      queryReq.statGroups = statGroups;
      const queryResp = await this.doQuery(queryReq);
      queryResp.statGroups?.forEach(statGroup=>{
        this.onStatGroupResultChanged(statGroup);
      })
      return queryResp.statGroups;
    } catch (error) {
      console.error(error);
      this.reportErrorFn(error);
    } finally {
      // this.isLoading = false;
    }
  }

  onStatGroupResultChanged(statGroupResult: StatGroupResult) {
    // console.log(`onStatGroupResultChanged() statGroupResult:`, statGroupResult);
    const statGroups = this.statGroups.filter(statGroup=>statGroup.id == statGroupResult.id);
    if ( statGroups.length === 1 ) {
      statGroups[0].visible = statGroupResult.visible;
      // console.log(`onStatGroupResultChanged() statGroups[0].visible:`, statGroups[0].visible, statGroups[0].id);
    } else {
      this.reportErrorFn(`Stat group "${statGroupResult.id}" not found (cannot update visibility after statitics have been updated)`);
    }
  }

  async load(filters: TableViewFilter[] = null) {
    //console.log(`load()`);
    //const loadStartTimeMs = performance.now();
    this.allRowsCheckboxValue = false;
    this.checkedRecs = [];
    this.isLoading = true;
    this.errorMsg = "";
    this.latestLoadRequestId = "" + new Date().getTime();

    try {
      const queryReq = this.buildRequest(filters, this.latestLoadRequestId);
      if ( this.statsEnabled && this.statGroups?.length ) {
        const duplicateIds = _(this.statGroups)
          .countBy('id')
          .pickBy((count) => count > 1)
          .keys()
          .value();
        if ( duplicateIds.length ) {
          throw new Error(`Invalid statistics request. Duplicate stat group ids: ${duplicateIds.join(',')}`);
        }
        queryReq.statGroups = [...this.statGroups].filter(sg=>sg.visible);
      }
      this.lastFilterValues = JSON.parse(JSON.stringify(filters));
      //
      // Request to server
      //
      const queryResp = await this.doQuery(queryReq);

      if (queryResp.requestId == queryReq.requestId) {
        if ( !this.meta && queryResp.meta ) {
          this.meta = queryResp.meta;
          const selectedView = this.meta.alternateViews?.find(
            v=>v.name===this.selectedAlternateViewName);
          this.selectedAlternateViewCssClass = selectedView?.cssClass;
          this.selectedAlternateViewTitle = selectedView?.title;
        }
        if ( this.statGroups?.length ) {
          this.statGroupResults = [...this.statGroups].map( origStatGroup => {
            const respStatGroup = queryResp.statGroups?.find( sg => sg.id == origStatGroup.id );
            if ( respStatGroup ) {
              return { ...origStatGroup, ...respStatGroup };
            } else {
              return { ...origStatGroup, rows: [] };
            }
          });
        } else {
          // this case is for dialer results table which generates stats via typescript in client          
          this.statGroupResults = queryResp.statGroups;
        }
        this.tableRowCount = queryResp.tableRowCount;
        this.queryRowCount = queryResp.queryRowCount;
        if (this.pagingEnabled && this.pageSize) {
          this.pageCount = Math.ceil(this.queryRowCount / this.pageSize);
        } else {
          this.pageCount = queryResp.rows ? 1 : 0;
        }
        this.pageNumber = queryResp.pageNumber;
        this.records = queryResp.rows;
        const virtualColNames =  Object.keys(this.virtualColNames);
        if ( virtualColNames.length && this.records ) {
          for ( const rec of this.records ) {
            for ( const virtualColName of virtualColNames) {
              rec[virtualColName] = rec[this.virtualColNames[virtualColName]];
            }
          }
        }

        this.groupedByDay = queryResp.groupedByDay
        //console.dir(queryResp)
        if (
          this.firstRowFilters?.length &&
          queryResp.selectedIndices?.length &&
          queryResp.selectedIndices[0] == -1
        ) {
          this.appState.notifyWarning(
            "Could not find rows for " + filtersToString(this.firstRowFilters),
            this,
            10
          );
        }
        this.selectedIndices = queryResp.selectedIndices;
        this.applySelectedIndices({selectedIndices:this.selectedIndices});
        this.isLoading = false;
        this.firstRowFilters = null;
        this.updateContext();
        this.queryResp$.emit(queryResp);
        //pager lifecycle issue workaround
        timer(0).subscribe((_) => {
          this.pageNumber = queryResp.pageNumber;
        });
      } else {
        throw new Error(
          `'${this.getName()}' REST response ID mismatch (req: ${
            queryReq.requestId
          }, resp:${queryResp.requestId})`
        );
      }
    } catch (error) {
      this.isLoading = false;
      console.error(error);
      this.reportErrorFn(error);
    }
    this.populatedFilterCnt = this.buildPopulatedFilters().length;
    //const loadEndTimeMs = performance.now();
    //console.log('load() elapsed secs:', (loadEndTimeMs-loadStartTimeMs)/1000.0)
    this.records$.emit(this.records)
  }

  applySelectedRecords(args: {
    recs:RecordType[],
    clearPreviousSelection?:boolean,
    scrollIntoViewMode?: 'start' | 'center' | 'end' | 'nearest' | false,
  }) {
    const {recs, clearPreviousSelection, scrollIntoViewMode} = args;
    const selectedIndices = this.records.map((r, index) => {
      return recs.includes(r) ? index : -1;
    }).filter(index => index !== -1);
    this.applySelectedIndices({selectedIndices,clearPreviousSelection,scrollIntoViewMode});
  }

  applySelectedIndices(args: {
    selectedIndices:number[],
    clearPreviousSelection?:boolean,
    scrollIntoViewMode?: 'start' | 'center' | 'end' | 'nearest' | false,
  }) {
    const {selectedIndices, clearPreviousSelection=true, scrollIntoViewMode} = args;
    if (clearPreviousSelection) {
      this.records.forEach((r) => {
        delete (r as any)._is_selected;
      });
    }
    if (!selectedIndices?.length) {
      return
    }
    selectedIndices.forEach((i) => {
      if (i >= 0 && i < this.records.length) {
        (this.records[i] as any)._is_selected = true;
      }
    });
    if (scrollIntoViewMode === false){
      return;
    }
    // scroll to first selected row after _is_selected causes tr.selected to be added to dom
    timer(0).subscribe((_) => {
      const element = this.elementRef?.nativeElement;
      const selectedRowElement = element.querySelector(
        "table.table-view-table tr.selected"
      );
      if (selectedRowElement ) {
        selectedRowElement.scrollIntoView({
          behavior: "smooth",
          block: scrollIntoViewMode || "center",
        });
      }
    });
  }

  updateColumnsForView(viewName:string){
    // override this if this.tableColumns needs to be altered based on current view
  }

  defaultCalendarItemIconClass = 'fa fa-star text-info'
  // change this in derived classes to customize calendar events
  calendarEventBuilder = ( rec: RecordType ) => {
    const ymdNoTzHack = (rec[this.calendarColumn.name]||'')
      .split("-")
      .map((str) => parseInt(str));
    const start = ymdNoTzHack.length > 1 ? new Date(
      ymdNoTzHack[0],
      (ymdNoTzHack[1]||1) - 1,
      ymdNoTzHack[2]
    ) : null;
    return {
      title: this.idColumns.map(c=>rec[c.name]).join(','),
      start,
      meta: {
        rec,
        iconClass: this.defaultCalendarItemIconClass
      }
    }
  }

  calendarEvents$ : Observable<CalendarEvent<{rec: RecordType, iconClass: string}>[]> = this.records$.pipe(
    map( records => {
      const eventBuilder = this.calendarEventBuilder
      return records.map( eventBuilder )
    }))

  //updateRows( rowCriteria: {})

  isSelectedIndex(index: number) {
    return this.selectedIndices?.includes(index);
  }

  firstRowFilters: TableViewFilter[] = null;
  public parseContextJson(strJson) {
    //console.log(`parseContextJson(${strJson})`)
    try {
      const context = JSON.parse(strJson);
      this.setContext(context);
      this.allVisible = this.visibleColumns.length == this.tableColumns.length;
    } catch (error) {
      this.appState.publishError(error, this);
    }
  }

  setContext(context: TableViewContext) {
    this.initPristine();
    const strContext = JSON.stringify(context);
    // console.log(`setContext(${strContext})`);
    if ( !this.defaultVisibleColumnsIndices ) {
      this.defaultVisibleColumnsIndices = this.getVisibleIndices();
    }
    context = JSON.parse(strContext); // makes a copy

    const fieldNames = this.tableColumns
      .map((column) => column.name)
      .filter((n) => n);
    for (const fieldName of fieldNames) {
      let fieldFilter: TableViewFilter;
      const column = this.tableColumns.find((c) => c.name == fieldName);

      let contextFieldFilter = context?.filters?.find(
        (f) => f && f.key == fieldName
      );
      if (contextFieldFilter) {
        if (
          typeof contextFieldFilter === "string" ||
          contextFieldFilter instanceof String
        ) {
          const value: string = contextFieldFilter as unknown as string;
          contextFieldFilter = { key: fieldName, value, mode: "contains" };
        }
        column.filter = contextFieldFilter;
        fieldFilter = contextFieldFilter;
      } else {
        const pf = this.pristineSettings.filters?.find(f=>f.key===column.name);
        if (pf) {
          column.filter = {...pf};
          fieldFilter = column.filter;
        }
      }
      if (!fieldFilter) {
        fieldFilter = { key: fieldName, value: "", mode: "contains" };
        column.filter = fieldFilter;
      }
      if (!fieldFilter.mode) {
        fieldFilter.mode = "contains";
      }
      if (!fieldFilter.value) {
        fieldFilter.value = "";
      }
    }

    if (context) {
      if (context.visible) {
        this.tableColumns.forEach((column) => {
          column.visible = context.visible.includes(column.name);
        });
      }
      this.statsEnabled = context.statsEnabled === true;
      if (context.sorts?.length) {
        this.sortColumns = context.sorts;
      }
      if ( context.selectedViewName ) {
        this.selectedAlternateViewName = context.selectedViewName;
      }
      this.updateColumnsForView(this.selectedAlternateViewName);
      if (context.visibleNames) {
        if ( !context.visibleIndices ) {
          context.visibleIndices = [];
        }
        context.visibleNames.forEach(colName=>{
          const colIndex = this.tableColumns.findIndex(col=>col.name===colName);
          if ( colIndex != -1 ) {
            context.visibleIndices.push(colIndex);
          }
        });
        context.visibleIndices = _.uniq(context.visibleIndices);
      }
      if (context.visibleIndices) {
        this.tableColumns.forEach((column, index) => {
          column.visible = context.visibleIndices.includes(index);
        });
      }
      if ( context.calendarColumnName ) {
        this.calendarColumn = this.tableColumns.find(c=>c.name===context.calendarColumnName)
        this.bShowCalendar = !!this.calendarColumn
      }
      const pageInfo = context.page;
      if (pageInfo) {
        if (pageInfo.size !== undefined ) {
          this.pageSize = pageInfo.size;
        }
        if (pageInfo.firstRowFilters) {
          this.firstRowFilters = pageInfo.firstRowFilters;
        }
        if (pageInfo.number) {
          this.pageNumber = pageInfo.number;
        }
      }
      if (context.selectedIndices) {
        this.selectedIndices = context.selectedIndices;
      }
      this.visibleColumns = this.tableColumns.filter((c)=>!!c.visible);
    }
  }

  //configEditComplete$ = new EventEmitter<TableViewConfigComponent>()

  configModalRef: BsModalRef;
  showConfigModal(template: TemplateRef<any>) {
    this.configModalRef = this.modalService.show(template, {
      class: "modal-xl",
      //initialState: {
      //  editComplete$: this.configEditComplete$
      //}
    });
  }

  async closeConfigModal(configComponent: TableViewConfigComponent) {
    console.log(`closeConfigModal(${configComponent})`);
    let bModified = configComponent.isModified();
    if (bModified) {
      configComponent.applyChanges();
    }
    this.configModalRef?.hide();
    this.configModalRef = null;
    if (bModified) {
      await this.load();
    }
  }

  showTab(viewName: string, itemId: string = null) {
    this.showTabFn(new ShowRequest("tab", viewName, itemId));
  }

  lastContext: string = null;

  getVisibleIndices() {
    let visibleIndices = [];
    this.tableColumns.forEach((tableColumn, index) => {
      if (tableColumn.visible) {
        visibleIndices.push(index);
      }
    });
    return visibleIndices;
  }

  buildPopulatedFilters = () =>
    this.tableColumns
      .filter(
        (c) => c.filter && (c.filter.value || c.filter.mode != "contains" || this.pristineSettings.filters?.find(f=>f.key==c.filter.key))
      )
      .map((c) => {
        const cfilter = c.filter;
        const tz = c.timeZone || (UTC_TEMPLATES.includes(c.template)&&'utc');
        if ( tz ) {
          cfilter.timeZone = tz;
        }
        return cfilter;
      });

  getContext = (): TableViewContext => {
    var filters = this.buildPopulatedFilters();
    var visibleIndices = this.getVisibleIndices();

    // no need to put visible columns in context if same as the defaults (make url smaller)
    if (visibleIndices.equals(this.defaultVisibleColumnsIndices)) {
      visibleIndices = [];
    }

    var sorts = this.sortColumns?.length ? this.sortColumns : null;

    const bPageSizeSet = this.pageSize != this.defaultPageSize;

    var context: Partial<TableViewContext> =
      filters.length ||
      visibleIndices.length ||
      sorts ||
      bPageSizeSet ||
      this.pageNumber != 1 ||
      this.selectedIndices?.length
        ? {}
        : null;
    if (context) {
      if (filters.length) {
        context.filters = filters;
      }
      if (visibleIndices.length) {
        context.visibleIndices = visibleIndices;
      }
      if (sorts) {
        context.sorts = sorts;
      }
      if ( this.selectedAlternateViewName ) {
        context.selectedViewName = this.selectedAlternateViewName;
      }
      context.page = {
        number: this.pageNumber,
      };
      if (bPageSizeSet) {
        context.page.size = this.pageSize;
      }
      if (this.firstRowFilters) {
        context.page.firstRowFilters = this.firstRowFilters;
      }
      if (this.selectedIndices?.length) {
        context.selectedIndices = this.selectedIndices;
      }
      if ( this.bShowCalendar && this.calendarColumn ) {
        context.calendarColumnName = this.calendarColumn.name
      }
      if ( this.statsEnabled ) {
        context.statsEnabled = this.statsEnabled;
      }
    }
    // console.log('getContext()')
    // console.dir(context)
    return context as TableViewContext;
  };

  updateContext = () => {
    const context = this.getContext();
    //console.log(JSON.stringify(context))
    const strContext = context ? JSON.stringify(context) : null;
    if (this.lastContext != strContext) {
      this.lastContext = strContext;
      this.contextChangeEmitter.emit(strContext);
    }
  };

  onRowCheckboxChange(record: RecordType) {
    this.checkedRecs = this.records.filter(
      (rec) => rec["_is_checked"] === true
    );
    this.allRowsCheckboxValue = this.checkedRecs.length == this.records.length;
  }

  onAllRowsCheckboxChange(event) {
    this.records.forEach(
      (rec) => (rec["_is_checked"] = this.allRowsCheckboxValue)
    );
    this.checkedRecs = this.allRowsCheckboxValue ? [...this.records] : [];
  }

  showDetailModal = ( record: any ) => {
    const modalRef = this.modalService.show(RecordDetailModalComponent, {
      ignoreBackdropClick: true,
      id: 20230804,
      class: "modal-xl",
      initialState: {
        title: this.getName(),
        record,
        table: this,
      }
    });
  }

  showRequestFn = (req: ShowRequest) => {
    this.showTabFn(req);
  };

  sort(columnName: string) {
    this.pageNumber = 1;
    if (!this.sortColumns) {
      this.sortColumns = [];
    }
    if (!this.sortColumns.length) {
      this.sortColumns = [{ key: columnName, order: "ASC" }];
    } else if (columnName == this.sortColumns[0]?.key) {
      this.sortColumns[0].order =
        this.sortColumns[0].order == "ASC" ? "DESC" : "ASC";
    } else {
      this.sortColumns = [
        { key: columnName, order: "ASC" },
        ...this.sortColumns.filter((c) => c.key != columnName),
      ];
    }
    if (this.sortColumns.length > this.maxSortColumns) {
      this.sortColumns.pop();
    }
    this.sortColumns.forEach( sortCol => {
      const tc = this.tableColumns.find(tc=>tc.name===sortCol.key);
      if ( tc?.smallNullSort === false ) {
        sortCol.smallNulls = false;
      }
    });
    //console.log(`sort(columnName:${columnName}), maxLen: ${this.maxSortColumns}, ${JSON.stringify(this.sortColumns)}`)
    this.load();
  }

  onPageNumberChanged(evt: { itemsPerPage: number; page: number }) {
    console.log(
      `onPageNumberChanged(itemsPerPage:${evt?.itemsPerPage},page:${evt?.page})`
    );
    if (this.pageNumber != evt.page) {
      this.pageNumber = evt.page;
      this.load();
    }
  }

  allVisible = false;
  allColumnCheckboxChange(event) {
    this.tableColumns.forEach(
      (tableColumn) => (tableColumn.visible = this.allVisible)
    );
    this.columnStateChanged(null);
  }

  public onTabSelected = (bSelected: boolean) => {
    this.isActiveView = bSelected;
    if (bSelected) {
      this.initialize();
    }
  };

  public isDateColumn = (column: TableColumn) => {
    return ["fulltime", "date","time"].includes(column.template);
  };

  bDownloading = false;
  public download = async () => this.exportAsCsv(); //of(this.exportAsXlsx())

  public exportAsXlsx = () => {
    this.bDownloading = true;
    try {
      const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(
        this.elementRef.nativeElement
      );
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, this.getName());
      var wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      saveAs(blob, this.getName() + ".xlsx");
    } catch (error) {
      console.error(error);
      this.reportErrorFn(error);
    }
    this.bDownloading = false;
  };


  public exportAsCsv = async () => {
    this.bDownloading = true;
    try {
      const filters = this.buildPopulatedFilters().filter((f) => f.value || ['empty','not empty'].includes(f.mode)).map(f=>{
        const realName = this.virtualColNames[f.key];
        return realName ? { ...f, key: realName } : f;
      });
      const sorts = _.uniqBy(this.sortColumns.map(sc=>{
        const realName = this.virtualColNames[sc.key];
        return realName ? { ...sc, key: realName } : sc;
      }), (sc)=>sc.key) || [];
      const queryReq: TableViewQueryRequest = {
        requestId: "" + new Date().getTime(),
        filters,
        sorts,
      };

      queryReq.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      console.log(`exportAsCsv(${JSON.stringify(queryReq)})`);
      const queryResp = await this.doQuery(queryReq);
      if (queryResp.requestId == queryReq.requestId) {
        const records = queryResp.rows;
        const virtualColNames =  Object.keys(this.virtualColNames);
        if ( virtualColNames.length && records ) {
          for ( const rec of records ) {
            for ( const virtualColName of virtualColNames) {
              rec[virtualColName] = rec[this.virtualColNames[virtualColName]];
            }
          }
        }
        const fieldNames = this.visibleColumns.map((c) => c.name);
        let csv = records.map((row) =>
          fieldNames
            .map((fieldName, index) => {
              let fieldValue = row[fieldName];

              const isDate = this.isDateColumn(this.visibleColumns[index]);
              if (isDate) {
                fieldValue = this.datePipe.transform(
                  fieldValue,
                  "yyyy-MM-dd HH:mm:ss.SSS",
                  "UTC"
                );
              }

              const replacer = (key, value) => (value === null ? "" : value);
              return JSON.stringify(fieldValue, replacer);
            })
            .join(",")
        );
        csv.unshift(fieldNames.join(","));
        let csvArray = csv.join("\r\n");

        var blob = new Blob([csvArray], { type: "text/csv" });
        saveAs(blob, this.getName() + ".csv");
      }
    } catch (error) {
      console.error(error);
      this.reportErrorFn(error);
    }
    this.bDownloading = false;
  };

  calendarColumn : TableColumn
  bShowCalendar : boolean = false
  onCalendarIconClick( column: TableColumn ) {
    console.log(`toggleCalendar(${column?.name})`)
    if ( column == this.calendarColumn ) {
      this.bShowCalendar = !this.bShowCalendar
      if ( !this.bShowCalendar ) {
        this.calendarColumn = null
      }
    } else {
      if ( this.calendarColumn?.filter ) {
        // this is another column that was previously selected (clear its column filter)
        this.calendarColumn.filter.value = ''
        this.calendarColumn.filter.mode = 'contains'
      }
      this.calendarColumn = column
      this.bShowCalendar = true
    }
  }

  closeCalender = ( closeEvent: { clearFilter: boolean }) => {
    this.bShowCalendar = false
    if ( closeEvent.clearFilter ) {
      this.calendarDateFilterChanged( { pattern: '', mode: 'contains'})
    }
    this.calendarColumn = null
  }


  calendarDateFilterChanged = ( filter : { pattern: string, mode: TableViewFilterMode }) => {
    console.log(`calendarDateFilterChanged(${JSON.stringify(filter)})`)
    this.calendarColumn.filter.mode = filter.mode;
    this.calendarColumn.filter.value = filter.pattern
    asyncScheduler.schedule( () => {
      this.load()
    })
  }

  onFilterModeChanged( column: TableColumn, mode: TableViewFilterMode){
    if ( column.filter ) {
      const lastMode = column.filter.mode
      if ( lastMode != mode ) {
        column.filter.mode = mode
        asyncScheduler.schedule( () => {
          this.load()
        })
      }
    }
  }

  selectedAlternateViewCssClass: string;

  onAlternateViewChanged( selectedViewName: string ) {
    if ( !selectedViewName ) {
      if ( this.pristineSettings.sorts ) {
        this.sortColumns = [...this.pristineSettings.sorts];
      }
      if ( this.pristineSettings.filters ) {
        this.tableColumns.forEach(tc=>{
          const pristineFilter = this.pristineSettings.filters.find(pf=>pf.key===tc.name);
          tc.filter = pristineFilter && {...pristineFilter};
        });
      }
      if ( this.pristineSettings.visibleColNames ) {
        this.tableColumns.forEach(tc=>{
          tc.visible = this.pristineSettings.visibleColNames.some(vcn=>vcn===tc.name);
        });
      }
    }

    this.bShowCalendar = false;
    this.selectedAlternateViewName = selectedViewName;

    const view = this.meta?.alternateViews?.find(v=>v.name===selectedViewName);
    this.selectedAlternateViewTitle = view?.title;
    this.selectedAlternateViewCssClass = view?.cssClass;
    this.updateColumnsForView(selectedViewName);

    if ( view ) {
      if ( view.visibility ) {
        const { remove, add, show } = view.visibility;
        if ( show?.length ) {
          this.tableColumns.forEach( tc => {
            tc.visible = show.some(cn=>cn===tc.name);
          })
        }
        add?.forEach( colName => {
          const tc = this.tableColumns.find(tc=>tc.name===colName);
          if ( tc ) {
            tc.visible = true;
          }
        });
        remove?.forEach( colName => {
          const tc = this.tableColumns.find(tc=>tc.name===colName);
          if ( tc ) {
            tc.visible = false;
          }
        });
      }
      for( const column of this.tableColumns ) {
        const tcFilter = column.filter;
        const vcFilter = view.filters?.find(f=>f.key===tcFilter?.key);
        if ( vcFilter ) {
          column.filter = {...vcFilter};
        } else {
          column.filter = undefined;
        }
      }
      if ( view.sorts ) {
        this.sortColumns = [...view.sorts];
      }
    }

    asyncScheduler.schedule( () => {
      this.load()
    })
  }

  async onStatsEnabledClick() {
    this.statsEnabled = !this.statsEnabled;
    if ( this.statsEnabled ) {
      await this.load();
    } else {
      this.updateContext();
    }
  }

  public getName = () => "table"; // override for each class so csv export file has better name
}
